<template>
	<div
		class="locations-page"
		:class="{
			'locations-page--geo-loading': loading,
		}"
	>
		<div class="divider">
			<div class="divider__left">
					<div class="location-box insicht insicht--fade-in insicht--fast">
							<div class="location-box__wrapper">
									<h2 class="location-box__title" v-html="$t('locations.title')"/>
									<div class="location-box__content">
											<div class="location-box__links">
													<Button
														class="location-box__link"
														icon="geo-location"
														:text="$t('locations.geoButton')"
														type="button"
														level="tertiary"
														arrow
														v-if="geolocationAvailable"
														@click="geolocationRequest"
													/>

											</div>
									</div>
							</div>
					</div>
			</div>
			<div class="divider__right">
        <!-- <div class="location-map"></div> -->
				<div
					class="location-grid"
					:class="{
						'location-grid--geo-loading': loading,
					}"
				>
					<div
						class="location-grid__item insicht insicht--fade-in"
						v-for="location in processedLocations"
						:key="location.id"
					>
						<div class="location-grid__item-wrapper">
								<component
									class="location-grid__item-image"
									:is="location.url ? 'a' : 'div'"
									:href="location.url"
									:target="location.isExternal ? '_blank' : null"
								>
									<img
										class="location-grid__item-image-element"
										:srcset="location.image.srcset"
										:src="location.image.src"
										alt="Location Image"
										data-object-fit
										loading="lazy"
									>
								</component>

								<component
									class="location-grid__item-title"
									:is="location.url ? 'a' : 'div'"
									:href="location.url"
									:target="location.isExternal ? '_blank' : null"
									v-html="location.title"
								/>
								<ul class="location-grid__item-icons">
										<li
											class="location-grid__item-icon"
											v-if="location.telephone"
										>
												<a
													class="location-grid__item-icon-link"
													:href="telUrl(location.telephone)"
												>
														<SVGPhone/>
												</a>
										</li>
										<li
											class="location-grid__item-icon"
											v-if="location.googleMapsUrl"
										>
												<a
													class="location-grid__item-icon-link"
													:href="location.googleMapsUrl"
													target="_blank"
												>
														<SVGLocation/>
												</a>
										</li>
										<li
											class="location-grid__item-icon"
											v-if="location.menuUrl"
										>
												<a
													class="location-grid__item-icon-link"
													:href="location.menuUrl"
												>
														<SVGMenu/>
												</a>
										</li>
								</ul>
								<div class="location-grid__item-links">
									<!-- TODO: this (home) url should be read from the config -->
									<Button
										v-if="location.openingHours.length && !location.isExternal"
										class="location-grid__item-link"
										:text="$t('locations.orderButton')"
										type="button"
										level="tertiary"
										:href="`/${language}`"
										arrow
									/>
									<Button
										v-else-if="location.isExternal && location.url"
										class="location-grid__item-link"
										:text="$t('locations.toWebsite')"
										level="tertiary"
										:href="location.url"
										:target="location.isExternal ? '_blank' : null"
										arrow
									/>
									<Button
										class="location-grid__item-link"
										:text="$t('locations.reserveButton')"
										type="button"
										level="tertiary"
										arrow
										v-if="location.reservationUrl"
										@click.prevent="onReservation(location.id)"
									/>
								</div>
								<div class="location-grid__item-openings">
									<template v-if="location.openingHours.length && !location.isExternal">
										<table class="location-grid__item-openings-table">
												<tr
													class="location-grid__item-openings-table-row"
													v-for="(opening, index) in location.openingHours"
													:key="`${index}-${opening.day}`"
												>
														<td
															class="location-grid__item-openings-table-cell"
															v-html="opening.day"
														/>
														<td
															class="location-grid__item-openings-table-cell"
															v-html="opening.times"
														/>
												</tr>
										</table>
										<div
											class="location-grid__item-openings-message"
											v-html="location.openTimesHtml"
										/>
									</template>
									<div
										v-if="location.openingHours.length < 0 && !location.isExternal"
										class="location-grid__item-openings-message"
										v-html="$t('locations.closedTitle')"
									/>
								</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import htmlComponent from 'htmlcomponent'
import Button from '@/components/Button';

export default {
	components: {
		Button,
	},
	data() {
		return {
			processedLocations: this.$root.options.locations || [],
			geolocationAvailable: 'geolocation' in navigator,
		};
	},
	computed: {
		...mapGetters('loader', [
			'isLoading',
		]),
		loading: {
			set(value) {
				this.$store.dispatch('loader/setLoading', value);
				this.$emit('loading', value);
			},
			get() {
				return this.isLoading;
			}
		},
	},
	methods: {
		telUrl(tel) {
			return `tel:${tel.replace(/\s/ig, '')}`;
		},
		onReservation(id) {
			htmlComponent.send('overlay:open', {type: 'reservation', locationId: id});
		},
		geolocationRequest() {
			this.loading = true;
			// open chrome with the following command to test this locally (Mac)
			// open -a /Applications/Google\ Chrome.app --args --unsafely-treat-insecure-origin-as-secure="http://madamesum.username.local"
			navigator.geolocation.getCurrentPosition(this.geolocationSuccess.bind(this), this.geolocationError.bind(this));
		},
		geolocationSuccess(position) {
			this.calculateDistance(position.coords);
			this.loading = false;
		},
		geolocationError() {
			this.geolocationAvailable = false;
			this.loading = false;
		},
		calculateDistance({latitude, longitude}) {
			// console.log('geo location calucation')
			this.processedLocations.forEach(location => {

				// calucation algorithm from here
				// https://stackoverflow.com/a/26837460

				const radlat1 = Math.PI * latitude / 180;
				const radlat2 = Math.PI * location.latitude / 180;
				const radlon1 = Math.PI * longitude / 180;
				const radlon2 = Math.PI * location.longitude / 180;
				const theta = longitude - location.longitude;
				const radtheta = Math.PI * theta  /  180;
				let dist = Math.sin(radlat1) * Math.sin(radlat2) + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
				dist = Math.acos(dist);
				dist = dist * 180 / Math.PI;
				dist = dist * 60 * 1.1515;
				dist = dist * 1.609344;
				location.distance = dist;
			});
			this.sortLocations();
		},
		sortLocations() {
			this.processedLocations.sort((a, b) => {
				return a.distance - b.distance;
			});
		},
	},
	created() {
		this.loading = true;
	},
	mounted() {
		this.loading = false;
	}
}
</script>

<style>

</style>
